import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, kindToAdjustmentMap, MarginSchemeKindItem, MarginSchemeKindQuery, NumberingItem, NumberingQuery, PaymentMethodItem, PaymentMethodQuery, SaleKindBO } from '@skylab-shared/business';
import { EventBusBase, IdentifiableValueConverter } from '@skylab-shared/core';
import { CalculationMethod, LabeledEnumValue, LabeledEnumValueConverter, SaleDocumentKind, SaleKindModel } from '@skylab-shared/dataModel';

import { SaleKindRepository, SaleKindRepositoryBase } from '@skylab-angular-shared/business';
import { CommandBus, EnumTranslator, EnumTranslatorBase, EventBus, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, FocusByDirective, FormContainerComponent, NumericValueAccessorDirective, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface SaleKindDialogResult {
    id: number;
    version: string;
}

interface SaleKindDialogViewModel extends DictionaryDialogViewModelBase<SaleKindBO> {
    calculationMethods: LabeledEnumValue[];
    calculationMethodsConverter: LabeledEnumValueConverter<CalculationMethod>;
    documentName: string;
    documentKindValueConverter: LabeledEnumValueConverter<SaleDocumentKind>;
    paymentMethods: PaymentMethodItem[];
    paymentMethodsConverter: IdentifiableValueConverter | null;
    numberings: NumberingItem[];
    adjustmentNumberings: NumberingItem[];
    numberingsConverter: IdentifiableValueConverter | null;
    marginSchemeKinds: MarginSchemeKindItem[];
    marginSchemeKindsConverter: IdentifiableValueConverter | null;
    vatMarginInvoice: boolean;
    isAdvance: boolean;
    isReceipt: boolean;
    isProFormInvoice: boolean;
    isInvoice: boolean;
    focusReady: boolean;
    useCompanyTransactionLocation: { value: boolean; };
    isAdjustmentNumberingVisible: () => boolean;
    isAutoPaymentVisible: () => boolean;
    isCalculationMethodVisible: () => boolean;
    getDefaultDueDateInnerLabel: () => string;
}

@Component({
    selector: 'skl-sale-kind-dialog',
    templateUrl: './sale-kind-dialog.component.html',
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, FocusByDirective, TextValueAccessorDirective, FormsModule, ValidateDirective, SelectValueAccessorDirective, NumericValueAccessorDirective, BooleanValueAccessorDirective, ButtonComponent, TranslatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SaleKindDialogComponent extends DictionaryDialogComponentBase
<
SaleKindModel,
SaleKindBO,
SaleKindRepositoryBase,
SaleKindDialogViewModel,
DictionaryDialogData<string>
> {
    public constructor(
        @Inject(SaleKindRepository) saleKindRepositoryBase: SaleKindRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        toastService: ToastService,
        commandBus: CommandBus,

        dialogService: DialogInsService,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase
    ) {
        super(saleKindRepositoryBase, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override getInitialState(): SaleKindDialogViewModel {
        return {
            ...super.getInitialState(),
            calculationMethods: this.enumTranslator.getLabeledEnumValueArray(CalculationMethod).filter(le => ![CalculationMethod.Manual, CalculationMethod.Margin].includes(le.value as CalculationMethod)),
            calculationMethodsConverter: new LabeledEnumValueConverter(this.enumTranslator, CalculationMethod),
            documentName: '',
            documentKindValueConverter: new LabeledEnumValueConverter(this.enumTranslator, SaleDocumentKind),
            paymentMethods: [],
            paymentMethodsConverter: null,
            numberings: [],
            numberingsConverter: null,
            marginSchemeKinds: [],
            marginSchemeKindsConverter: null,
            vatMarginInvoice: SaleDocumentKind.VatMarginInvoice === this.data.id,
            isAdvance: SaleDocumentKind.AdvanceInvoice === this.data.id,
            isReceipt: SaleDocumentKind.Receipt === this.data.id,
            isProFormInvoice: SaleDocumentKind.ProForma === this.data.id,
            isInvoice: SaleDocumentKind.Invoice === this.data.id,
            getDefaultDueDateInnerLabel: this.getDefaultDueDateInnerLabel.bind(this),
            isAdjustmentNumberingVisible: () => this.data.id !== SaleDocumentKind.ProForma,
            isAutoPaymentVisible: () => this.data.id === SaleDocumentKind.AdvanceInvoice,
            isCalculationMethodVisible: () => {
                const companyParams = this.localContext.getPreloaded(CompanyParametersQuery);
                return !!companyParams?.vatPayer && (this.viewModel.isInvoice || this.viewModel.isProFormInvoice || this.viewModel.isAdvance);
            }
        };
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            super.preloadDataAsync(),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(MarginSchemeKindQuery),
            this.localContext.preload(CompanyParametersQuery)
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        const self = this;
        this.viewModel.useCompanyTransactionLocation = {
            get value() {
                return !self.viewModel.bo.model.useCustomTransactionLocation;
            },
            set value(value) {
                self.viewModel.bo.model.useCustomTransactionLocation = !value;
            }
        };
        this.viewModel.documentName = this.enumTranslator.getLabeledEnumValue(SaleDocumentKind, this.viewModel.bo.model.name).label;

        const paymentMethods = this.localContext.getPreloaded(PaymentMethodQuery) || [];
        this.viewModel.paymentMethods = paymentMethods;
        this.viewModel.paymentMethodsConverter = new IdentifiableValueConverter(paymentMethods);

        const documentKind = this.isAdvance(<SaleDocumentKind>this.viewModel.bo.model.name)
            ? SaleDocumentKind.Invoice
            : this.viewModel.bo.model.name;

        const numberings = this.localContext.getPreloaded(NumberingQuery) || [];
        this.viewModel.numberings = numberings.filter(n => n.kinds.indexOf(documentKind) > -1);
        const numberingKind = (documentKind in kindToAdjustmentMap) ? (kindToAdjustmentMap as any)[documentKind] : null;
        this.viewModel.adjustmentNumberings = numberingKind ? numberings.filter(n => n.kinds.indexOf(numberingKind) > -1) : [];
        this.viewModel.numberingsConverter = new IdentifiableValueConverter(numberings);

        if (this.viewModel.vatMarginInvoice) {
            this.viewModel.marginSchemeKinds = this.localContext.getPreloaded(MarginSchemeKindQuery) || [];
            this.viewModel.marginSchemeKindsConverter = new IdentifiableValueConverter(this.viewModel.marginSchemeKinds);
        }
    }

    public getDefaultDueDateInnerLabel(): string {
        return this.viewModel.bo && this.viewModel.bo.model
            ? this.viewModel.bo.model.defaultDueDate === 1
                ? this.l10n._T('scenes.configuration.shared.document-configuration.dialog.form.default-payment-term.day')
                : this.l10n._T('scenes.configuration.shared.document-configuration.dialog.form.default-payment-term.days')
            : '';
    }

    public isAdvance(documentKind: SaleDocumentKind): boolean {
        return documentKind === SaleDocumentKind.AdvanceInvoice;
    }

    protected override get dictionaryToLoadProperties(): { id: number, [key: string]: string | number} | null {
        return this.data?.id ? { id: null as any, name: this.data.id } : null;
    }
}
